// © 2026 Orpyx Medical Technologies Inc.

import {IPrimaryConcernDto, ISuggestedActionDto} from "doctors-dashboard/http-clients/index";

/// <summary>
/// <para>Heading for suggested actions the API sent without an owner. The schema allows a null
/// owner even though every action is expected to carry "Orpyx" or "Provider/Clinic".</para>
/// </summary>
export const UnownedSuggestedActionsLabel = 'Other';

/// <summary>
/// <para>One owner's suggested actions, as they are listed under that owner's heading.</para>
/// </summary>
export class SuggestedActionGroup {
    readonly owner: string;
    readonly actions: ISuggestedActionDto[];

    constructor(owner: string, actions: ISuggestedActionDto[]) {
        this.owner = owner;
        this.actions = actions;
    }
}

/// <summary>
/// <para>The suggested actions the selected concern offers, empty when it offers none.</para>
/// <para>An empty list is what hides the field, and it is also what makes the field optional,
/// mirroring how primaryConcernsForAction drives the concern field.</para>
/// </summary>
export const suggestedActionsForConcern = (concerns: IPrimaryConcernDto[], primaryConcernId: string | undefined): ISuggestedActionDto[] => {
    if (!primaryConcernId)
        return [];

    const selectedConcernId: number = parseInt(primaryConcernId, 10);
    if (isNaN(selectedConcernId))
        return [];

    return concerns.find(concern => concern.id === selectedConcernId)?.suggestedActions ?? [];
};

/// <summary>
/// <para>Splits the actions into one group per owner, for the selector's headings.</para>
/// <para>The API already sorts the list, so groups are emitted in the order their owner first
/// appears and each group keeps the API's order. O(n) time and space.</para>
/// </summary>
export const groupSuggestedActionsByOwner = (actions: ISuggestedActionDto[]): SuggestedActionGroup[] => {
    const actionsByOwner: Map<string, ISuggestedActionDto[]> = new Map<string, ISuggestedActionDto[]>();

    for (const action of actions) {
        const owner: string = action.actionOwner || UnownedSuggestedActionsLabel;
        const ownerActions: ISuggestedActionDto[] | undefined = actionsByOwner.get(owner);

        if (ownerActions) {
            ownerActions.push(action);
        } else {
            actionsByOwner.set(owner, [action]);
        }
    }

    return Array.from(actionsByOwner, ([owner, ownerActions]) => new SuggestedActionGroup(owner, ownerActions));
};
